/**
 * A PROPOSED PLAN IS A LIST OF DECISIONS (spec §61B).
 *
 * Existing is what is there. A Proposed design is Existing plus intentional
 * decisions: this window goes, that wall arrives, the vanity moves. Each of
 * those decisions must be reversible on its own, months later, long after
 * any undo history has gone — and reverting one is NOT Ctrl+Z:
 *
 *   Ctrl+Z          undoes the last thing you did, in this sitting.
 *   Revert change   cancels ONE decision, whenever you made it, and puts
 *                   the inherited thing back exactly as Existing has it.
 *
 * Nothing new is stored to make this work. The decisions ARE the difference
 * between the two plans, which the Phase 9 diff engine already computes from
 * root lineage — this module reads that difference and says, for each one,
 * what cancelling it would mean. Reverting a removal restores the archived
 * copy (identity, lineage and everything bound to it intact); reverting an
 * addition archives the thing the plan added; reverting a change puts
 * Existing's own numbers back.
 *
 * Pure: no React, no DOM, no SQL.
 */
import { diffPlans, type ChangeFinding, DEFAULT_DIFF_TOLERANCE_PU } from "./diff";
import {
  archiveObject,
  archiveOpening,
  archiveWall,
  moveObject,
  moveOpening,
  resizeObject,
  restoreObject,
  restoreOpening,
  restoreWall,
  rotateObject,
  setObjectConfig,
  setOpeningProps,
  mergeChanges,
  type OpContext,
  type OpResult,
} from "./operations";
import type { PlanAnalysis } from "./analysis";
import { regionAt } from "./analysis";
import type { PlanModel, PlanObject, Wall, WallOpening } from "./model";
import { objectPose } from "./objects";

export type ProposedEditKind = "added" | "removed" | "moved" | "resized" | "modified";

export type RevertAction =
  /** Put the inherited entity back (cancel a removal). */
  | "restore"
  /** Take out what this plan added. */
  | "remove"
  /** Put Existing's own numbers back. */
  | "restore-properties"
  /** Honest refusal: the plan changed too much to undo this one decision. */
  | "unavailable";

export interface ProposedEdit {
  /** Stable across recomputation, so a list does not jump around. */
  key: string;
  entityKind: "wall" | "opening" | "object";
  kind: ProposedEditKind;
  /** Root lineage id: the thing itself, across both plans. */
  rootId: string;
  /** The plan's own copy, when it has one. */
  proposedId: string | null;
  /** Existing's copy, when there is one. */
  existingId: string | null;
  /** "Remove Window", "Add Wall", "Move Vanity" — never lineage or ids. */
  summary: string;
  /** "Bedroom", "Kitchen" — where it is, when that is knowable. */
  where: string | null;
  /** The sign shown beside it: − removed, + added, ~ changed. */
  sign: "-" | "+" | "~";
  revert: RevertAction;
  /** What the button says: "Cancel Removal", "Revert to Existing"… */
  revertLabel: string;
  /** Why it cannot be reverted, when it cannot. */
  reason: string | null;
}

export interface ProposedEditsInput {
  existing: PlanModel | null;
  proposed: PlanModel;
  /** Of the PROPOSED plan — used only to say which room something is in. */
  analysis?: PlanAnalysis | null;
  roomNames?: ReadonlyMap<string, string>;
  /** definitionId → display name ("Vanity", "Base Cabinet"). */
  objectNames?: ReadonlyMap<string, string>;
  tolerancePu?: number;
}

const rootOf = (e: { id: string; originId: string | null }): string => e.originId ?? e.id;

function byRoot<T extends { id: string; originId: string | null }>(items: Iterable<T>): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const item of items) {
    const key = rootOf(item);
    const list = out.get(key) ?? [];
    list.push(item);
    out.set(key, list);
  }
  return out;
}

function openingNoun(o: WallOpening | undefined): string {
  if (!o) return "opening";
  return o.kind === "door" ? "Door" : o.kind === "window" ? "Window" : "Opening";
}

function objectNoun(o: PlanObject | undefined, names: ReadonlyMap<string, string> | undefined): string {
  if (!o) return "Object";
  return names?.get(o.definitionId) ?? "Object";
}

function roomOf(
  analysis: PlanAnalysis | null | undefined,
  point: { x: number; y: number } | null,
  roomNames: ReadonlyMap<string, string> | undefined,
): string | null {
  if (!analysis || !point || !roomNames) return null;
  const region = regionAt(analysis, point);
  if (!region || region.roomIds.length !== 1) return null;
  return roomNames.get(region.roomIds[0]) ?? null;
}

function openingPoint(model: PlanModel, o: WallOpening): { x: number; y: number } | null {
  const wall = model.walls.get(o.wallId);
  if (!wall) return null;
  const a = model.nodes.get(wall.startNodeId);
  const b = model.nodes.get(wall.endNodeId);
  if (!a || !b) return null;
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  if (len === 0) return null;
  const t = (o.offsetFromStart + o.width / 2) / len;
  return { x: Math.round(a.x + (b.x - a.x) * t), y: Math.round(a.y + (b.y - a.y) * t) };
}

/**
 * Every intentional difference between this plan and Existing, in the order
 * a person would read them: what was taken out, what was added, what moved.
 */
export function listProposedEdits(input: ProposedEditsInput): ProposedEdit[] {
  const { existing, proposed } = input;
  const tol = input.tolerancePu ?? DEFAULT_DIFF_TOLERANCE_PU;
  const findings = diffPlans(existing, proposed, { tolerancePu: tol });

  const proposedWalls = byRoot([...proposed.walls.values()]);
  const proposedOpenings = byRoot([...proposed.openings.values()]);
  const proposedObjects = byRoot([...proposed.objects.values()]);
  const existingOpenings = existing ? byRoot([...existing.openings.values()]) : new Map<string, WallOpening[]>();
  const existingObjects = existing ? byRoot([...existing.objects.values()]) : new Map<string, PlanObject[]>();

  const edits: ProposedEdit[] = [];
  const seen = new Set<string>();

  for (const finding of findings) {
    const kind = editKind(finding);
    if (!kind) continue;
    const key = `${finding.entityKind}:${finding.rootId}`;
    if (seen.has(key)) continue;
    seen.add(key);

    const proposedId = finding.proposedIds[0] ?? null;
    const existingId = finding.baselineIds[0] ?? null;

    if (finding.entityKind === "opening") {
      const prop = proposedId ? proposed.openings.get(proposedId) : undefined;
      const archivedCopy = (proposedOpenings.get(finding.rootId) ?? []).find((o) => o.archivedAt !== null);
      const exist = existingId && existing ? existing.openings.get(existingId) : undefined;
      const noun = openingNoun(prop ?? exist ?? archivedCopy);
      const source = prop ?? archivedCopy;
      const where = source
        ? roomOf(input.analysis, openingPoint(proposed, source), input.roomNames)
        : null;
      edits.push(describe(kind, "opening", finding.rootId, prop?.id ?? archivedCopy?.id ?? null, exist?.id ?? null, noun, where, !!exist));
      continue;
    }

    if (finding.entityKind === "object") {
      const prop = proposedId ? proposed.objects.get(proposedId) : undefined;
      const archivedCopy = (proposedObjects.get(finding.rootId) ?? []).find((o) => o.archivedAt !== null);
      const exist = existingId && existing ? existing.objects.get(existingId) : undefined;
      const noun = objectNoun(prop ?? exist ?? archivedCopy, input.objectNames);
      const source = prop ?? archivedCopy;
      const where = source ? roomOf(input.analysis, objectPose(proposed, source).center, input.roomNames) : null;
      edits.push(describe(kind, "object", finding.rootId, prop?.id ?? archivedCopy?.id ?? null, exist?.id ?? null, noun, where, !!exist));
      continue;
    }

    // Walls: added and removed are reversible; a wall the plan MOVED is not
    // undone one decision at a time, because the geometry around it moved
    // with it. Saying so is better than pretending (spec §61B).
    const propWall = proposedId ? proposed.walls.get(proposedId) : undefined;
    const archivedWall = (proposedWalls.get(finding.rootId) ?? []).find((w) => w.archivedAt !== null);
    const existWall = existingId && existing ? existing.walls.get(existingId) : undefined;
    edits.push(describeWall(kind, finding.rootId, propWall?.id ?? archivedWall?.id ?? null, existWall?.id ?? null, !!existWall));
  }

  // ---- Decisions the SCOPE diff deliberately does not report ----------
  //
  // A resized vanity implies no demolition, so the Phase 9 diff calls it
  // "Keep" — correct for cost, and useless for a person asking what this
  // plan changed. Comparing the plan's copy with Existing's own copy, by
  // lineage, answers that without touching what the scope engine believes.
  if (existing) {
    for (const [root, copies] of proposedOpenings) {
      const key = `opening:${root}`;
      if (seen.has(key)) continue;
      const plan = copies.find((o) => o.archivedAt === null);
      const source = (existingOpenings.get(root) ?? []).find((o) => o.archivedAt === null);
      if (!plan || !source) continue;
      const kind = openingDifference(source, plan, tol);
      if (!kind) continue;
      seen.add(key);
      const where = roomOf(input.analysis, openingPoint(proposed, plan), input.roomNames);
      edits.push(describe(kind, "opening", root, plan.id, source.id, openingNoun(plan), where, true));
    }
    for (const [root, copies] of proposedObjects) {
      const key = `object:${root}`;
      if (seen.has(key)) continue;
      const plan = copies.find((o) => o.archivedAt === null);
      const source = (existingObjects.get(root) ?? []).find((o) => o.archivedAt === null);
      if (!plan || !source) continue;
      const kind = objectDifference(source, plan, tol);
      if (!kind) continue;
      seen.add(key);
      const where = roomOf(input.analysis, objectPose(proposed, plan).center, input.roomNames);
      edits.push(describe(kind, "object", root, plan.id, source.id, objectNoun(plan, input.objectNames), where, true));
    }
  }

  // Openings and objects the plan REMOVED leave no active entity, so the
  // diff sees them through Existing. Anything the diff classified we have;
  // anything else (a plan that archived its inherited copy while Existing
  // still has it) is added here from lineage directly.
  if (existing) {
    for (const [root, copies] of existingOpenings) {
      const source = copies.find((o) => o.archivedAt === null);
      if (!source) continue;
      const key = `opening:${root}`;
      if (seen.has(key)) continue;
      const planCopies = proposedOpenings.get(root) ?? [];
      const live = planCopies.find((o) => o.archivedAt === null);
      const archived = planCopies.find((o) => o.archivedAt !== null);
      if (live || !archived) continue;
      seen.add(key);
      edits.push(
        describe("removed", "opening", root, archived.id, source.id, openingNoun(source), null, true),
      );
    }
    for (const [root, copies] of existingObjects) {
      const source = copies.find((o) => o.archivedAt === null);
      if (!source) continue;
      const key = `object:${root}`;
      if (seen.has(key)) continue;
      const planCopies = proposedObjects.get(root) ?? [];
      const live = planCopies.find((o) => o.archivedAt === null);
      const archived = planCopies.find((o) => o.archivedAt !== null);
      if (live || !archived) continue;
      seen.add(key);
      edits.push(
        describe("removed", "object", root, archived.id, source.id, objectNoun(source, input.objectNames), null, true),
      );
    }
  }

  const order: Record<ProposedEditKind, number> = { removed: 0, added: 1, moved: 2, resized: 3, modified: 4 };
  return edits.sort((a, b) => order[a.kind] - order[b.kind] || a.summary.localeCompare(b.summary));
}

/** What this plan did to an inherited opening, or null if nothing. */
function openingDifference(source: WallOpening, plan: WallOpening, tol: number): ProposedEditKind | null {
  if (
    source.kind !== plan.kind ||
    source.swing !== plan.swing ||
    source.windowConfig !== plan.windowConfig ||
    source.windowOperation !== plan.windowOperation
  ) {
    return "modified";
  }
  if (
    Math.abs(source.width - plan.width) > tol ||
    Math.abs(source.height - plan.height) > tol ||
    Math.abs(source.sillHeight - plan.sillHeight) > tol
  ) {
    return "resized";
  }
  if (Math.abs(source.offsetFromStart - plan.offsetFromStart) > tol) return "moved";
  return null;
}

/** What this plan did to an inherited object, or null if nothing. */
function objectDifference(source: PlanObject, plan: PlanObject, tol: number): ProposedEditKind | null {
  if ((source.config ?? null) !== (plan.config ?? null) || source.rotationX10 !== plan.rotationX10) return "modified";
  if (Math.abs(source.width - plan.width) > tol || Math.abs(source.depth - plan.depth) > tol) return "resized";
  if (Math.hypot(source.x - plan.x, source.y - plan.y) > tol) return "moved";
  return null;
}

function editKind(finding: ChangeFinding): ProposedEditKind | null {
  switch (finding.classification) {
    case "added":
      return "added";
    case "removed":
      return "removed";
    case "moved":
      return "moved";
    case "resized":
      return "resized";
    case "modified":
      return "modified";
    default:
      return null; // unchanged / review-only carry no decision to reverse
  }
}

function describe(
  kind: ProposedEditKind,
  entityKind: "opening" | "object",
  rootId: string,
  proposedId: string | null,
  existingId: string | null,
  noun: string,
  where: string | null,
  inherited: boolean,
): ProposedEdit {
  const base = { key: `${entityKind}:${rootId}`, entityKind, kind, rootId, proposedId, existingId, where } as const;
  switch (kind) {
    case "removed":
      return {
        ...base,
        summary: `Remove ${noun}`,
        sign: "-",
        revert: proposedId ? "restore" : "unavailable",
        revertLabel: "Cancel Removal",
        reason: proposedId ? null : "This plan has no copy of it to put back.",
      };
    case "added":
      return {
        ...base,
        summary: `Add ${noun}`,
        sign: "+",
        revert: proposedId ? "remove" : "unavailable",
        revertLabel: "Remove Again",
        reason: proposedId ? null : null,
      };
    default:
      return {
        ...base,
        summary: `${kind === "moved" ? "Relocate" : kind === "resized" ? "Resize" : "Change"} ${noun}`,
        sign: "~",
        revert: inherited && proposedId ? "restore-properties" : "unavailable",
        revertLabel: "Revert to Existing",
        reason: inherited ? null : "This one was never in Existing, so there is nothing to revert to.",
      };
  }
}

function describeWall(
  kind: ProposedEditKind,
  rootId: string,
  proposedId: string | null,
  existingId: string | null,
  inherited: boolean,
): ProposedEdit {
  const base = { key: `wall:${rootId}`, entityKind: "wall" as const, kind, rootId, proposedId, existingId, where: null };
  if (kind === "removed") {
    return {
      ...base,
      summary: "Remove Wall",
      sign: "-",
      revert: proposedId ? "restore" : "unavailable",
      revertLabel: "Cancel Removal",
      reason: proposedId ? null : "This plan has no copy of it to put back.",
    };
  }
  if (kind === "added") {
    return {
      ...base,
      summary: "Add Wall",
      sign: "+",
      revert: proposedId ? "remove" : "unavailable",
      revertLabel: "Remove Again",
      reason: null,
    };
  }
  return {
    ...base,
    summary: kind === "moved" ? "Move Wall" : "Change Wall",
    sign: "~",
    revert: "unavailable",
    revertLabel: "Revert to Existing",
    reason: inherited
      ? "Moving a wall moves what joins it, so put it back on the plan rather than undoing it here."
      : "This wall was never in Existing.",
  };
}

/**
 * Carry out one revert, as ordinary geometry operations, so it autosaves,
 * undoes and re-scopes exactly like any other edit.
 */
export function revertProposedEdit(
  model: PlanModel,
  ctx: OpContext,
  edit: ProposedEdit,
  existing: PlanModel | null,
): OpResult {
  if (edit.revert === "unavailable" || !edit.proposedId) {
    throw new Error(edit.reason ?? "That change cannot be reverted here.");
  }
  const id = edit.proposedId;

  if (edit.revert === "restore") {
    if (edit.entityKind === "wall") return restoreWall(model, ctx, id);
    if (edit.entityKind === "opening") return restoreOpening(model, ctx, id);
    return restoreObject(model, ctx, id);
  }

  if (edit.revert === "remove") {
    if (edit.entityKind === "wall") return archiveWall(model, ctx, id);
    if (edit.entityKind === "opening") return archiveOpening(model, ctx, id);
    return archiveObject(model, ctx, id);
  }

  // restore-properties: Existing's own numbers, put back.
  if (!existing || !edit.existingId) throw new Error("Existing no longer has that to revert to.");

  if (edit.entityKind === "opening") {
    const source = existing.openings.get(edit.existingId);
    const target = model.openings.get(id);
    if (!source || !target) throw new Error("That opening is no longer in both plans.");
    const props = setOpeningProps(model, ctx, id, {
      kind: source.kind,
      width: source.width,
      height: source.height,
      sillHeight: source.sillHeight,
      swing: source.swing,
      windowConfig: source.windowConfig,
      windowOperation: source.windowOperation,
    });
    const moved = moveOpening(props.model, ctx, id, source.offsetFromStart);
    return { model: moved.model, changes: mergeChanges([...props.changes, ...moved.changes]) };
  }

  const source = existing.objects.get(edit.existingId);
  const target = model.objects.get(id);
  if (!source || !target) throw new Error("That object is no longer in both plans.");
  let result: OpResult = { model, changes: [] };
  const step = (next: OpResult) => {
    result = { model: next.model, changes: mergeChanges([...result.changes, ...next.changes]) };
  };
  if (source.width !== target.width || source.depth !== target.depth) {
    step(resizeObject(result.model, ctx, id, { width: source.width, depth: source.depth }));
  }
  if (source.rotationX10 !== target.rotationX10 && !target.hostWallId) {
    step(rotateObject(result.model, ctx, id, source.rotationX10));
  }
  if (source.x !== target.x || source.y !== target.y) {
    step(moveObject(result.model, ctx, id, { x: source.x, y: source.y }));
  }
  if ((source.config ?? null) !== (target.config ?? null)) {
    step(setObjectConfig(result.model, ctx, id, source.config ?? null));
  }
  if (result.changes.length === 0) {
    throw new Error("That is already the same as Existing.");
  }
  return result;
}

/** How the badge counts them: "3 changes from Existing". */
export function describeEditCount(edits: readonly ProposedEdit[]): string {
  return `${edits.length} change${edits.length === 1 ? "" : "s"} from Existing`;
}

export type { Wall };

/* ------------------------------------------------------------------ *
 * Clicking a drawn removal (spec §70B)
 * ------------------------------------------------------------------ */

/**
 * The removal under a point, if any.
 *
 * A proposed removal is ARCHIVED in the plan, so the ordinary hit test
 * cannot see it — clicking the dashed removed wall fell straight through
 * to the room region, and the one thing the drawing invited you to click
 * was the one thing you could not select. This resolves a click against
 * the SAME geometry the overlay draws: the Existing plan's copy of each
 * removed entity.
 *
 * Pure, and only ever called for points the live hit test resolved to a
 * region or nothing — live walls, openings and objects still win first,
 * exactly as specific-beats-container demands.
 */
export function removalAt(
  edits: readonly ProposedEdit[],
  existing: PlanModel,
  p: { x: number; y: number },
  tolerancePu: number,
): ProposedEdit | null {
  let best: { edit: ProposedEdit; d: number } | null = null;
  const consider = (edit: ProposedEdit, d: number, limit: number) => {
    if (d <= limit && (!best || d < best.d)) best = { edit, d };
  };
  for (const edit of edits) {
    if (edit.kind !== "removed" || !edit.existingId) continue;
    if (edit.entityKind === "wall") {
      const wall = existing.walls.get(edit.existingId);
      const a = wall && existing.nodes.get(wall.startNodeId);
      const b = wall && existing.nodes.get(wall.endNodeId);
      if (!wall || !a || !b) continue;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const lenSq = dx * dx + dy * dy;
      const u = lenSq === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq));
      const d = Math.hypot(a.x + u * dx - p.x, a.y + u * dy - p.y);
      consider(edit, d, Math.max(tolerancePu, wall.thickness / 2 + tolerancePu / 2));
    } else if (edit.entityKind === "opening") {
      const opening = existing.openings.get(edit.existingId);
      const at = opening ? openingPoint(existing, opening) : null;
      if (at) consider(edit, Math.hypot(at.x - p.x, at.y - p.y), tolerancePu * 2);
    } else {
      const object = existing.objects.get(edit.existingId);
      if (object) consider(edit, Math.hypot(object.x - p.x, object.y - p.y), tolerancePu * 2);
    }
  }
  return best !== null ? (best as { edit: ProposedEdit; d: number }).edit : null;
}
